import { Text } from '@chakra-ui/react'
import { flatMap, groupBy, max, min } from 'lodash-es'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { capitalizeWords, isNotNull, wordWithArticle } from '@/utils/helpers'

import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import type { ProgramSlug } from '@/client/services/programs/constants'
import { arePrePostSurveysPausedForProgram } from '@/client/services/programs/helpers'
import type {
  ProgramDetails,
  ProgramIteration,
  ProgramOfferingInstance,
} from '@/client/services/programs/types'
import { getPersonalizedSurveyUrl, getTutorOfferingPageUrl } from '@/client/services/url'
import { getUserLevelInWorld, isLevelAtLeastMinLevel } from '@/client/services/worlds'

import useProgramIterationOpenForRegistration from '@/components/Programs/hooks/useProgramIterationOpenForRegistration'

import { Priority, type TodoItem } from '../../types'
import buildSurveyTodoItems from '../buildSurveyTodoItems'
import type { SurveyTodoDetails, TodosSupportedProgramSlug } from './types'

const SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END = 5
const POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END = 8
const DAYS_TO_INCLUDE_SERIES_AFTER_END = 10

type ProgramSeriesHostDetails = {
  seriesId: number
  completedPreSeriesSurvey: boolean
  completedPostSeriesSurvey: boolean
  seriesStartDate: DateTime
  seriesEndDate: DateTime
  offering: ProgramOfferingInstance
  // TODO: we might only need iterationSlug here
  programIteration: ProgramIteration
}

function getSurveyTodosForIteration({
  iteration,
  seriesHostDetails,
  programType,
  programSlug,
  uuidTraining,
  worldSlug,
  now,
}: {
  iteration: ProgramIteration
  seriesHostDetails: ProgramSeriesHostDetails[]
  programType: ProgramDetails['programType']
  programSlug: ProgramSlug
  uuidTraining: string
  worldSlug: string
  now: DateTime
}): SurveyTodoDetails[] {
  if (arePrePostSurveysPausedForProgram(programSlug)) {
    return []
  }

  const { tutorPreSeriesSurveyUrl, tutorPostSeriesSurveyUrl } = iteration

  // We only have one pre and post series survey, even if a tutor is hosting multiple series this iteration
  const completedTutorPreSeriesSurvey = seriesHostDetails
    .map(host => host.completedPreSeriesSurvey)
    .includes(true)
  const completedTutorPostSeriesSurvey = seriesHostDetails
    .map(host => host.completedPostSeriesSurvey)
    .includes(true)

  const minSeriesStartDate = min(seriesHostDetails.map(host => host.seriesStartDate)) ?? null

  const showPostSeriesSurveyAfter = max(
    seriesHostDetails.map(details =>
      details.seriesEndDate.minus({ days: SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END })
    )
  )
  const showPostSeriesSurvey = showPostSeriesSurveyAfter != null && now > showPostSeriesSurveyAfter

  const postSeriesSurveyDeadline = max(
    seriesHostDetails.map(details =>
      details.seriesEndDate.plus({ days: POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END })
    )
  )

  const tutorPreSeriesSurveyTodo: SurveyTodoDetails | null =
    tutorPreSeriesSurveyUrl != null
      ? {
          title: `Complete the ${iteration.title} Pre-Survey`,
          subtitle: 'To help us improve the tutor experience!',
          completed: completedTutorPreSeriesSurvey,
          deadline: minSeriesStartDate,
          surveyUrl: tutorPreSeriesSurveyUrl,
          modalDetails: {
            key: `${programSlug}-${iteration.iterationSlug}-tutor-pre-survey`,
            title: `${iteration.title} ${capitalizeWords(programType)} Pre-Survey`,
            body: () => (
              <Text>
                Fill out this 1 minute pre-survey for the {iteration.title}{' '}
                {capitalizeWords(programType)}
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: tutorPreSeriesSurveyUrl,
              uuidTraining,
              worldSlug,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: seriesHostDetails[0].seriesId,
                surveyType: 'tutor-pre-survey',
              }),
          },
        }
      : null

  const tutorPostSeriesSurveyTodo: SurveyTodoDetails | null =
    tutorPostSeriesSurveyUrl != null && showPostSeriesSurvey
      ? {
          title: `Complete the ${iteration.title} Post-Survey`,
          subtitle: 'To help us improve the tutor experience!',
          completed: completedTutorPostSeriesSurvey,
          deadline: postSeriesSurveyDeadline,
          surveyUrl: tutorPostSeriesSurveyUrl,
          modalDetails: {
            key: `${programSlug}-${iteration.iterationSlug}-tutor-post-survey`,
            title: `${iteration.title} ${capitalizeWords(programType)} Post-Survey`,
            body: () => (
              <Text>
                Fill out this 1 minute post-survey for the {iteration.title}{' '}
                {capitalizeWords(programType)}
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: tutorPostSeriesSurveyUrl,
              uuidTraining,
              worldSlug,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: seriesHostDetails[0].seriesId,
                surveyType: 'tutor-post-survey',
              }),
          },
        }
      : null

  return [tutorPreSeriesSurveyTodo, tutorPostSeriesSurveyTodo].filter(isNotNull)
}

export function useProgramTutorTodos({
  worldSlug,
  programSlug,
  programType,
  iterations,
}: {
  worldSlug: string
  programSlug: TodosSupportedProgramSlug
  programType: ProgramDetails['programType']
  iterations: ProgramIteration[]
}): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const now = useServerNow()
  const [openModalKey, setOpenModalKey] = useState<string | null>(null)

  const programOpenIterationForTutors = useProgramIterationOpenForRegistration({
    programSlug,
    type: 'tutor',
  })

  const level = getUserLevelInWorld({ user, worldSlug })

  if (level == null) {
    return []
  }

  const isTutorInWorld = isLevelAtLeastMinLevel({ level, minLevel: 'tutor' })
  const worldMember = query.one(
    WorldMemberQ.where({
      userId: user.id,
      include: [
        {
          association: 'World',
          required: true,
          where: SQL`${WorldQ.slug} = ${worldSlug}`,
        },
      ],
    })
  )
  const expressedIntentToTutor = worldMember(WorldMemberQ.expressedIntentToTutor()) ?? false

  const upcomingSeriesHostsDetails: ProgramSeriesHostDetails[] = query
    .many(
      SeriesHostQ.where({
        userId: user.id,
        include: [
          {
            association: 'Series',
            required: true,
            include: [
              {
                association: 'World',
                required: true,
                where: SQL`${WorldQ.slug} = ${worldSlug}`,
              },
              {
                association: 'SeriesTemplate',
                required: false,
              },
            ],
            where: SQL`${SeriesQ.isActiveOrEndedWithinDays(DAYS_TO_INCLUDE_SERIES_AFTER_END)}`,
          },
        ],
      })
    )
    .map(host => {
      const seriesTemplateSlug = host(SeriesTemplateQ.slug)

      const offeringDetails = iterations
        .flatMap(iteration =>
          iteration.offerings.map(offering => ({
            programIteration: iteration,
            offering,
          }))
        )
        .find(({ offering }) => offering.seriesTemplateSlug === seriesTemplateSlug)

      return {
        seriesId: host(SeriesQ.id) ?? 0,
        completedPreSeriesSurvey: host(SeriesHostQ.hasCompletedPreSeriesSurvey()) ?? false,
        completedPostSeriesSurvey: host(SeriesHostQ.hasCompletedPostSeriesSurvey()) ?? false,
        seriesStartDate: DateTime.fromISO(`${host(SeriesQ.getComputedStartDate())}`),
        seriesEndDate: DateTime.fromISO(`${host(SeriesQ.getComputedEndDate())}`),
        offering: offeringDetails?.offering,
        programIteration: offeringDetails?.programIteration,
      }
    })
    .filter(
      (details): details is ProgramSeriesHostDetails =>
        details.offering != null && details.programIteration != null
    )

  const seriesHostDetailsByIteration = groupBy(
    upcomingSeriesHostsDetails,
    details => details.programIteration.iterationSlug
  )

  const surveyTodoDetails = flatMap(
    Object.entries(seriesHostDetailsByIteration),
    ([iterationSlug, seriesHostDetails]) => {
      const iteration = iterations.find(i => i.iterationSlug === iterationSlug)
      if (iteration == null) {
        // TODO: Handle this case
        return []
      }
      return getSurveyTodosForIteration({
        iteration,
        seriesHostDetails,
        programType,
        programSlug,
        uuidTraining: user.uuidTraining,
        worldSlug,
        now,
      })
    }
  )

  const surveyTodos = buildSurveyTodoItems({
    configs: surveyTodoDetails,
    worldSlug,
    targetLevel: 'tutor',
    openModalKey,
    setOpenModalKey,
  })

  const upcomingProgramIterationSlug = programOpenIterationForTutors?.iterationSlug

  const publishedUpcomingProgramSeries =
    upcomingProgramIterationSlug != null &&
    (seriesHostDetailsByIteration[upcomingProgramIterationSlug] ?? []).length > 0

  const publishSeriesTodo: TodoItem | null =
    programOpenIterationForTutors != null
      ? {
          worldSlug,
          title: `Publish ${wordWithArticle(
            programOpenIterationForTutors.title
          )} ${capitalizeWords(programType)}!`,
          completed: publishedUpcomingProgramSeries,
          link: getTutorOfferingPageUrl({ type: 'program', slug: worldSlug }),
          deadline: programOpenIterationForTutors.tutorRegistrationDeadline,
          priority: Priority.Medium,
          emoji: '📅',
          disabled: isTutorInWorld
            ? null
            : {
                tooltip: 'You must become a tutor in this subworld before publishing a series.',
              },
          targetLevel: 'tutor',
        }
      : null

  if (!expressedIntentToTutor && !isTutorInWorld) {
    return []
  }

  const todos = [publishSeriesTodo, ...surveyTodos]

  return todos.filter(isNotNull).filter(todo => todo.deadline == null || now < todo.deadline)
}
